import { type TJSONSchema, getJsonSchemaElement } from "@artifact-management/json-dynamic-form";

export interface IFormGenEnumOption {
  key: string;
  label: string;
  value: unknown;
}

// Multiple inputs hold their options on items, single ones on the element itself
const getFormGenOptionsSource = (jsonSchemaElement: TJSONSchema, multiple: boolean | undefined): TJSONSchema => {
  const items: unknown = jsonSchemaElement.items;
  return multiple && typeof items === "object" && items !== null && !Array.isArray(items)
    ? (items as TJSONSchema)
    : jsonSchemaElement;
};

export const getFormGenRawEnumOptions = (
  jsonSchemaElement: TJSONSchema,
  multiple: boolean | undefined,
  scope: string,
  parsedJsonSchema: TJSONSchema
): unknown[] => {
  const source = getFormGenOptionsSource(jsonSchemaElement, multiple);
  const entries =
    typeof source.setting === "object" ? getJsonSchemaElement(parsedJsonSchema, scope, multiple)?.enum : source.enum;
  return Array.isArray(entries) ? entries : [];
};

const isLabelledOption = (entry: unknown): entry is { value: string; label: string } =>
  typeof entry === "object" &&
  entry !== null &&
  typeof (entry as { value?: unknown }).value === "string" &&
  typeof (entry as { label?: unknown }).label === "string";

// Display labels for string enums, declared as oneOf: [{ const, title }] next to the enum
export const getFormGenEnumLabels = (
  jsonSchemaElement: TJSONSchema,
  multiple: boolean | undefined
): Record<string, string> => {
  const { oneOf } = getFormGenOptionsSource(jsonSchemaElement, multiple);
  if (!Array.isArray(oneOf)) {
    return {};
  }
  return Object.fromEntries(
    oneOf.flatMap((entry: unknown) => {
      const { const: value, title } = (entry ?? {}) as { const?: unknown; title?: unknown };
      return typeof value === "string" && typeof title === "string" ? [[value, title]] : [];
    })
  );
};

export const getFormGenEnumOptions = (
  entries: unknown[],
  labels: Record<string, string> = {}
): IFormGenEnumOption[] => {
  return entries.flatMap((entry): IFormGenEnumOption[] => {
    if (typeof entry === "string") {
      return [{ key: entry, label: labels[entry] ?? entry, value: entry }];
    }
    if (isLabelledOption(entry)) {
      return [{ key: entry.value, label: entry.label, value: entry }];
    }
    return [];
  });
};

// Maps a form value (string or labelled object) back to the option key driving the widget
export const getFormGenEnumOptionKey = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (isLabelledOption(value)) {
    return value.value;
  }
  return "";
};
